import React, { useMemo } from 'react';
import { Box, Typography, Paper } from '@mui/material';

function boundsFromGeometry(geom) {
  if (!geom?.nodes?.length) return { minX: 0, maxX: 10, minY: -2, maxY: 3, w: 10, h: 5 };
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  geom.nodes.forEach((n) => {
    minX = Math.min(minX, n.x);
    maxX = Math.max(maxX, n.x);
    minY = Math.min(minY, n.y);
    maxY = Math.max(maxY, n.y);
  });
  const pad = Math.max(maxX - minX, maxY - minY) * 0.12 || 0.5;
  return {
    minX: minX - pad,
    maxX: maxX + pad,
    minY: minY - pad,
    maxY: maxY + pad,
    w: maxX - minX + 2 * pad,
    h: maxY - minY + 2 * pad,
  };
}

function utilizationColor(u) {
  const x = Math.min(1.2, Math.max(0, u || 0));
  if (x < 0.5) return '#2e7d32';
  if (x < 0.9) return '#f9a825';
  return '#c62828';
}

function geometryHasPortalColumns(geom) {
  if (!geom?.members?.length) return false;
  return geom.members.some((m) => m.group === 'column');
}

/**
 * Post-analysis figure: member utilization widths, axial force labels, deflected shape overlay.
 * @param {boolean} showMemberKeys — label each bar with its geometry `key` (matches Member checks table).
 * @param {boolean} showMemberForces — when false (e.g. Deflection tab), hide member N labels and show nodal u in mm.
 */
export default function TrussResultsFigure({
  geometry,
  solution,
  designChecks,
  title = 'Analysis results',
  showMemberKeys = false,
  showMemberForces = true,
  /** If set (e.g. 95), Paper uses this % of parent width so the SVG can scale to ~full card width. */
  containerWidthPercent = null,
  /** Bottom-chord node ids to mark as K-joint / weld inspection points. */
  bottomJointMarkNodeIds = null,
  /** Top-chord node ids at K-joint brace ends (same weld note geometry). */
  topJointMarkNodeIds = null,
  /** Every node that is an end of a diagonal or vertical (labels for joints without a full K weld row). */
  braceEndpointNodeIds = null,
  selectedJointNodeId = null,
  onJointMarkerClick = null,
  onTopJointMarkerClick = null,
}) {
  const svgContent = useMemo(() => {
    if (!geometry?.nodes?.length) return null;
    const { minX, maxX, minY, maxY, w, h } = boundsFromGeometry(geometry);
    const W = 920;
    const hasPortal = geometryHasPortalColumns(geometry);
    const worldAR = h / Math.max(w, 1e-6);
    let H = Math.round(W * worldAR * 1.02);
    if (hasPortal) {
      H = Math.min(580, Math.max(360, H));
    } else {
      H = Math.min(400, Math.max(280, H));
    }
    const sx = W / w;
    const sy = H / h;
    const sc = Math.min(sx, sy);
    const ox = (W - (maxX - minX) * sc) / 2;
    const oy = (H - (maxY - minY) * sc) / 2;
    const tx = (x) => ox + (x - minX) * sc;
    const ty = (y) => oy + (maxY - y) * sc;

    const utilByKey = {};
    (designChecks || []).forEach((c) => {
      const k = c.member_key;
      if (k != null) utilByKey[k] = Math.max(utilByKey[k] || 0, Number(c.utilization) || 0);
    });

    const chordGroups = new Set([
      'bottom_chord',
      'top_chord',
      'top_chord_left',
      'top_chord_right',
      'arch_rib',
      'arch_leg',
    ]);
    const forceByKey = {};
    let nMax = 1;
    (solution?.truss_forces || []).forEach((f) => {
      const a = Math.abs(Number(f.N_kN) || 0);
      nMax = Math.max(nMax, a);
      forceByKey[`${f.i}-${f.j}`] = f.N_kN;
      forceByKey[`${f.j}-${f.i}`] = -f.N_kN;
    });
    (solution?.frame_forces || []).forEach((f) => {
      if (!chordGroups.has(f.group)) return;
      const Ni = Number(f.N_i_kN);
      const a = Math.abs(Ni) || 0;
      nMax = Math.max(nMax, a);
      forceByKey[`${f.i}-${f.j}`] = Ni;
      forceByKey[`${f.j}-${f.i}`] = -Ni;
    });

    const dispList = solution?.displacements || [];
    const dispByNode = {};
    dispList.forEach((d) => {
      if (d && d.node != null) dispByNode[d.node] = d;
    });
    const deflScale = Math.max(maxX - minX, maxY - minY, 1) * 0.02;

    const momentByKey = {};
    let mMax = 1;
    (solution?.frame_forces || []).forEach((f) => {
      if (!chordGroups.has(f.group)) return;
      const mEnd = Math.max(Math.abs(Number(f.M_i_kNm) || 0), Math.abs(Number(f.M_j_kNm) || 0));
      mMax = Math.max(mMax, mEnd);
      momentByKey[`${f.i}-${f.j}`] = Number(f.M_i_kNm) || 0;
    });
    const momentLines = (geometry.members || []).map((m, idx) => {
      if (!chordGroups.has(m.group)) return null;
      const a = geometry.nodes[m.i];
      const b = geometry.nodes[m.j];
      if (!a || !b) return null;
      const bend = momentByKey[`${m.i}-${m.j}`];
      if (bend == null || Math.abs(bend) < 1e-6) return null;
      const scale = (18 * bend) / mMax;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.hypot(dx, dy) || 1;
      const nx = -dy / len;
      const ny = dx / len;
      return (
        <line
          key={`m-${m.key || idx}`}
          x1={tx(a.x)}
          y1={ty(a.y)}
          x2={tx((a.x + b.x) / 2 + nx * (scale / sc))}
          y2={ty((a.y + b.y) / 2 + ny * (scale / sc))}
          stroke="#6a1b9a"
          strokeWidth={1.25}
        />
      );
    });

    const lines = (geometry.members || []).map((m, idx) => {
      const a = geometry.nodes[m.i];
      const b = geometry.nodes[m.j];
      if (!a || !b) return null;
      const fk = `${m.i}-${m.j}`;
      const util = utilByKey[m.key] != null ? utilByKey[m.key] : 0;
      const Nk = forceByKey[fk];
      const absN = Math.abs(Number(Nk) || 0);
      // Thickness from |N| (compression and tension equally); colour still from utilization.
      const strokeW = 1.5 + 6 * (absN / nMax);
      const stroke = utilizationColor(util);
      const midx = (a.x + b.x) / 2;
      const midy = (a.y + b.y) / 2;
      const baseW = m.group === 'column' ? 1.15 : 1;
      const vxW = (b.x - a.x) * sc;
      const vyW = -(b.y - a.y) * sc;
      const vlen = Math.hypot(vxW, vyW) || 1;
      const nxp = (-vyW / vlen) * 11;
      const nyp = (vxW / vlen) * 11;
      const keyStr = m.key != null ? String(m.key) : '';
      return (
        <g key={m.key || idx}>
          <line
            x1={tx(a.x)}
            y1={ty(a.y)}
            x2={tx(b.x)}
            y2={ty(b.y)}
            stroke={stroke}
            strokeWidth={strokeW * baseW}
            strokeLinecap="round"
          />
          {showMemberKeys && keyStr && (
            <text
              x={tx(midx) + nxp}
              y={ty(midy) + nyp}
              fontSize={8}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#0d47a1"
              stroke="#fff"
              strokeWidth={0.3}
              paintOrder="stroke"
            >
              {keyStr}
            </text>
          )}
          {showMemberForces && !showMemberKeys && Nk != null && m.group !== 'column' && (
            <text
              x={tx(midx)}
              y={ty(midy)}
              fontSize={9}
              textAnchor="middle"
              fill="#222"
              stroke="#fff"
              strokeWidth={0.35}
              paintOrder="stroke"
            >
              {Number(Nk).toFixed(1)}
            </text>
          )}
          {showMemberForces && showMemberKeys && Nk != null && m.group !== 'column' && (
            <text
              x={tx(midx) - nxp * 0.85}
              y={ty(midy) - nyp * 0.85}
              fontSize={8}
              textAnchor="middle"
              fill="#333"
              stroke="#fff"
              strokeWidth={0.3}
              paintOrder="stroke"
            >
              {Number(Nk).toFixed(1)}
            </text>
          )}
        </g>
      );
    });

    const topRoles = new Set(['top_chord', 'top_chord_left', 'top_chord_right']);
    const jointMarks = [];
    const labeledJointIds = new Set();
    if (Array.isArray(bottomJointMarkNodeIds) && bottomJointMarkNodeIds.length > 0) {
      bottomJointMarkNodeIds.forEach((rawId) => {
        const id = Number(rawId);
        const n = geometry.nodes[id];
        if (!n || n.role !== 'bottom_chord') return;
        labeledJointIds.add(id);
        const cx = tx(n.x);
        const cy = ty(n.y);
        const r = 12;
        const sel = selectedJointNodeId != null && Number(selectedJointNodeId) === id;
        jointMarks.push(
          <g
            key={`jmark-b-${id}`}
            style={{ cursor: onJointMarkerClick ? 'pointer' : 'default' }}
            onClick={(e) => {
              e.stopPropagation();
              if (onJointMarkerClick) onJointMarkerClick(id);
            }}
          >
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="rgba(255, 152, 0, 0.2)"
              stroke={sel ? '#bf360c' : '#f57c00'}
              strokeWidth={sel ? 3 : 2}
            />
            <text x={cx} y={cy - r - 5} fontSize={10} textAnchor="middle" fill="#e65100" fontWeight={sel ? 700 : 500}>
              {id}
            </text>
          </g>,
        );
      });
    }
    if (Array.isArray(topJointMarkNodeIds) && topJointMarkNodeIds.length > 0) {
      topJointMarkNodeIds.forEach((rawId) => {
        const id = Number(rawId);
        const n = geometry.nodes[id];
        if (!n || !topRoles.has(n.role)) return;
        labeledJointIds.add(id);
        const cx = tx(n.x);
        const cy = ty(n.y);
        const r = 11;
        jointMarks.push(
          <g
            key={`jmark-t-${id}`}
            style={{ cursor: onTopJointMarkerClick ? 'pointer' : 'default' }}
            onClick={(e) => {
              e.stopPropagation();
              if (onTopJointMarkerClick) onTopJointMarkerClick(id);
            }}
          >
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="rgba(3, 155, 229, 0.18)"
              stroke="#0277bd"
              strokeWidth={2}
            />
            <text x={cx} y={cy - r - 5} fontSize={10} textAnchor="middle" fill="#01579b" fontWeight={600}>
              {id}
            </text>
          </g>,
        );
      });
    }
    if (Array.isArray(braceEndpointNodeIds) && braceEndpointNodeIds.length > 0) {
      braceEndpointNodeIds.forEach((rawId) => {
        const id = Number(rawId);
        if (labeledJointIds.has(id)) return;
        const n = geometry.nodes[id];
        if (!n) return;
        labeledJointIds.add(id);
        const cx = tx(n.x);
        const cy = ty(n.y);
        const r = 10;
        const sel = selectedJointNodeId != null && Number(selectedJointNodeId) === id;
        jointMarks.push(
          <g key={`jmark-x-${id}`}>
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="rgba(120, 120, 120, 0.12)"
              stroke={sel ? '#424242' : '#757575'}
              strokeWidth={sel ? 2.5 : 1.5}
            />
            <text x={cx} y={cy - r - 5} fontSize={10} textAnchor="middle" fill="#424242" fontWeight={sel ? 700 : 500}>
              {id}
            </text>
          </g>,
        );
      });
    }

    const nodalDeflLabels = [];
    if (!showMemberForces && dispList.length > 0) {
      geometry.nodes.forEach((n) => {
        const d = dispByNode[n.id];
        if (!d) return;
        const uxs = Number(d.ux_mm);
        const uys = Number(d.uy_mm);
        if (!Number.isFinite(uxs) && !Number.isFinite(uys)) return;
        const cx = tx(n.x);
        const cy = ty(n.y);
        // Frame-plane solver: x = span (building X), y = height (building Z).
        const line1 = `ux ${Number.isFinite(uxs) ? uxs.toFixed(2) : '—'} mm`;
        const line2 = `uz ${Number.isFinite(uys) ? uys.toFixed(2) : '—'} mm`;
        nodalDeflLabels.push(
          <g key={`defln-${n.id}`}>
            <text
              x={cx + 8}
              y={cy - 4}
              fontSize={7.5}
              textAnchor="start"
              fill="#1565c0"
              stroke="#fff"
              strokeWidth={0.25}
              paintOrder="stroke"
            >
              {line1}
            </text>
            <text
              x={cx + 8}
              y={cy + 6}
              fontSize={7.5}
              textAnchor="start"
              fill="#1565c0"
              stroke="#fff"
              strokeWidth={0.25}
              paintOrder="stroke"
            >
              {line2}
            </text>
            <text x={cx} y={cy} fontSize={8} textAnchor="middle" fill="#0d47a1" fontWeight={600} stroke="#fff" strokeWidth={0.3} paintOrder="stroke">
              {n.id}
            </text>
          </g>,
        );
      });
    }

    const dispPoly = [];
    if (dispList.length > 0) {
      (geometry.members || []).forEach((m, idx) => {
        const a = geometry.nodes[m.i];
        const b = geometry.nodes[m.j];
        const da = dispByNode[m.i];
        const db = dispByNode[m.j];
        if (!a || !b || !da || !db) return;
        const ax = a.x + (Number(da.ux_mm) / 1000) * deflScale * 80;
        const ay = a.y + (Number(da.uy_mm) / 1000) * deflScale * 80;
        const bx = b.x + (Number(db.ux_mm) / 1000) * deflScale * 80;
        const by = b.y + (Number(db.uy_mm) / 1000) * deflScale * 80;
        dispPoly.push(
          <line
            key={`defl-${idx}`}
            x1={tx(ax)}
            y1={ty(ay)}
            x2={tx(bx)}
            y2={ty(by)}
            stroke="#6d4c41"
            strokeWidth={1.5}
            strokeDasharray="6 4"
            opacity={0.8}
          />,
        );
      });
    }

    return (
      <svg
        width="100%"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
      >
        {dispPoly}
        {momentLines}
        {lines}
        {jointMarks}
        {nodalDeflLabels}
        <text x={10} y={H - 10} fontSize={10} fill="#666">
          {!showMemberForces
            ? 'Blue: node id; ux / uz in mm (SLS). Brown dashed: exaggerated deformed shape.'
            : showMemberKeys
              ? 'Blue labels: member keys (same as table). Black numbers: N in kN (tension +). Brown dashed: deformed shape (exaggerated).'
              : bottomJointMarkNodeIds?.length ||
                  topJointMarkNodeIds?.length ||
                  braceEndpointNodeIds?.length
                ? [
                    bottomJointMarkNodeIds?.length
                      ? 'Orange: bottom K-joint nodes (table / sketch).'
                      : '',
                    topJointMarkNodeIds?.length ? 'Blue: top chord brace-end nodes.' : '',
                    braceEndpointNodeIds?.length
                      ? 'Grey: other diagonal/vertical ends (node id).'
                      : '',
                    'Black numbers: N in kN (tension +). Brown dashed: exaggerated deformed shape (SLS ref.).',
                  ]
                    .filter(Boolean)
                    .join(' ')
                : 'Forces in kN (tension +). Brown dashed: exaggerated deformed shape (SLS ref.).'}
        </text>
      </svg>
    );
  }, [
    geometry,
    solution,
    designChecks,
    showMemberKeys,
    showMemberForces,
    containerWidthPercent,
    bottomJointMarkNodeIds,
    topJointMarkNodeIds,
    braceEndpointNodeIds,
    selectedJointNodeId,
    onJointMarkerClick,
    onTopJointMarkerClick,
  ]);

  if (!geometry?.nodes?.length) return null;

  const paperSx =
    containerWidthPercent != null
      ? {
          p: 2,
          mt: 2,
          width: `${containerWidthPercent}%`,
          maxWidth: '100%',
          mx: 'auto',
          boxSizing: 'border-box',
        }
      : { p: 2, mt: 2 };

  return (
    <Paper sx={paperSx}>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      <Box sx={{ width: '100%', minHeight: 0 }}>{svgContent}</Box>
    </Paper>
  );
}
